import React, { Fragment, Component, useState, forwardRef, } from 'react';
import qs from 'qs';
import { Button, FormGroup, Label, Input, } from 'reactstrap';
import { groupBy, keyBy, last, isEqual, sortBy, omitBy, isUndefined, chunk, pick, mapValues, omit, fromPairs, } from 'lodash';
import { toast } from 'react-toastify';
import classnames from 'classnames';
import Select from 'react-select';
import { TextEncoder } from 'text-encoding';
import { unparse as unparseCsv, parse as parseCsv } from 'papaparse';
import fileDownload from 'js-file-download';
import { useToggle } from 'react-use';
import { Container, Draggable } from 'react-smooth-dnd';
import arrayMove from 'array-move';

import firebase from '../../firebase';
import { accountItemCategories, } from '../../shared/category';
import { serial, periodOfFiscalYear, } from '../../shared/util';
import { accountCategories, journalTags, accountItemSettingCsvUrls, } from '../../shared/config';
import { mapKeysToJa, mapKeysFromJa, } from '../../shared/texts';
import { usedSegments } from '../../shared/models/company';
import { fields, checkTargets, } from '../../shared/models/accountItemSetting';
import { batch, getCollectionData, } from '../../shared/firebase';
import HelpLink from '../HelpLink';
import CompanyPage from '../hocs/CompanyPage';
import { canReadAccountItemAuditorSetting } from '../../abilities';
import { readCsvFile, normalizeAccountItemsOrderSetting } from '../../utils';
import useQueryParams from '../hooks/useQueryParams';
import useScrollFocusByQuery from '../hooks/useScrollFocusByQuery';
import QuerySelector from '../QuerySelector';
import ModelFormModal from '../modals/ModelFormModal';
import EditButton from '../EditButton';
import ProgressButton from '../ProgressButton';
import ImportButton from '../ImportButton';
import ToTop from '../ToTop';

const db = firebase.firestore();
const companiesRef = db.collection('companies');
const { keys, entries } = Object;

function CompanyAccountItemSettings (props) {
  const { company, taxes, user, period, match: { params: { companyId } }, location, accountItemsGroupedByCategory, sortedAccountItems, accountItemSettingsById = {}, accountItemAuditorSettingsById = {}, accountItemSettings, accountItemAuditorSettings, accountItemsOrderSetting, customAccountItems = [], } = props;
  const accountItemsByName = keyBy(sortedAccountItems, 'name');
  const customAccountItemsById = keyBy(customAccountItems, 'id');
  const queryParams = useQueryParams();
  const [targetPeriod, setTargetPeriod] = useState();
  const [isImporting, toggleImporting] = useToggle(false);
  const displayJournalTags = omit(journalTags, [...(!usedSegments(company).includes(1) ? ['segment1s'] : []), ...(!usedSegments(company).includes(2) ? ['segment2s', 'segment3s'] : [])]);
  const availableTaxes = sortBy(taxes.filter(_ => _.available), 'name');
  const { accountItemNames, } = queryParams;
  const normalizedOrder = normalizeAccountItemsOrderSetting(accountItemsOrderSetting);
  const placedCustomAccountItemIds = new Set(Object.values(normalizedOrder).flatMap(_ => _.customAccountItem || []));
  const buildAccountItemRow = (accountName) => {
    const accountItemSetting = accountItemSettingsById[`${accountName.replace(/\//g, '_')}`];
    const accountItemAuditorSetting = accountItemAuditorSettingsById[`${accountName.replace(/\//g, '_')}__${period}`];
    return {
      rowType: 'accountItem',
      accountName,
      ...(canReadAccountItemAuditorSetting(user) && { threshold: accountItemAuditorSetting?.threshold }),
      // NOTE: noteがまだない場合があるので、`pick(accountItemSetting, ['note', ...])`のようにできない。エクスポートの考慮。
      ...['journalsCheckDimensions', ...keys(displayJournalTags).map(_ => `${_}JournalCheckTargets`), 'defaultDebitTaxes', 'defaultCreditTaxes', 'note'].reduce((x, y) => ({ ...x, [y]: accountItemSetting?.[y] }), {}),
      accountItemSetting,
      accountItemAuditorSetting,
    };
  };
  const rowGroups = accountItemCategories.map((category) => {
    const accountItems = (accountItemsGroupedByCategory[category.name] || []).filter(_ => _.available);
    const accountItemsByName = keyBy(accountItems, 'name');
    const orderEntries = accountItemsOrderSetting?.[category.name] || [];
    // NOTE: 新形式の並び順（{type, name|id}）をベースに、画面表示の混在順を構築する。
    // - 旧形式（string）は `accountItem` として扱う
    // - orderEntriesに含まれない通常科目は末尾に追加（新規科目への追従）
    // - orderEntriesに含まれない/該当カテゴリ未配置のカスタム科目はここでは扱わない（未配置エリアに表示）
    const orderedRows = orderEntries.flatMap((entry) => {
      if(typeof entry === 'string') {
        const ai = accountItemsByName[entry];
        return ai ? [buildAccountItemRow(entry)] : [];
      }
      if(entry?.type === 'accountItem') {
        const ai = accountItemsByName[entry.name];
        return ai ? [buildAccountItemRow(entry.name)] : [];
      }
      if(entry?.type === 'customAccountItem') {
        const customAccountItem = customAccountItemsById[entry.id];
        return customAccountItem ? [{ rowType: 'customAccountItem', customAccountItem }] : [];
      }
      return [];
    });
    const placedAccountItemNames = new Set(orderEntries.map(_ => typeof _ === 'string' ? _ : (_?.type === 'accountItem' ? _.name : null)).filter(_ => _));
    const remainingAccountItemRows = accountItems.filter(_ => !placedAccountItemNames.has(_.name)).map(_ => buildAccountItemRow(_.name));
    const rows = [...orderedRows, ...remainingAccountItemRows];
    let filteredRows = rows;
    if(queryParams.accountItemNames) {
      filteredRows = filteredRows.filter(_ => _.rowType === 'accountItem' ? queryParams.accountItemNames.includes(_.accountName) : queryParams.accountItemNames.includes(_.customAccountItem.name));
    }
    return {
      category,
      rows,
      filteredRows,
    };
  });
  const uncategorizedCustomAccountItems = customAccountItems.filter(_ => !placedCustomAccountItemIds.has(_.id));
  const onClickExport = async () => {
    const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
    // NOTE: 並び順を保ったまま通常科目とカスタム科目を混在出力する。カスタム科目は科目名（accountName）列のみ埋める。
    // NOTE: category列は、並び替えインポート時にカスタム科目のカテゴリ移動先として利用される（通常科目では無視される）。
    // NOTE: 未配置のカスタム科目もエクスポートに含める。カテゴリ未配置なのでcategory列は空にし、並び替えインポート時にカテゴリを指定して配置できるようにする。
    const uncategorizedCustomAccountItemRows = uncategorizedCustomAccountItems
      .filter(_ => !queryParams.accountItemNames || queryParams.accountItemNames.includes(_.name))
      .map(_ => ({ row: { rowType: 'customAccountItem', customAccountItem: _ }, category: '' }));
    const exportRows = [
      ...rowGroups.flatMap(_ => _.filteredRows.map(row => ({ row, category: _.category.name }))),
      ...uncategorizedCustomAccountItemRows,
    ].map(({ row, category }) => {
      if(row.rowType === 'customAccountItem') {
        return mapKeysToJa({ accountName: row.customAccountItem.name, category });
      }
      return mapKeysToJa(({
        ...omit(row, ['rowType', 'accountItemSetting', 'accountItemAuditorSetting']),
        category,
        journalsCheckDimensions: keys(displayJournalTags).filter(_ => row.journalsCheckDimensions ? row.journalsCheckDimensions.includes(_) : true),
        ...keys(displayJournalTags).reduce((x, k) => {
          return {
            ...x,
            [`${k}JournalCheckTargets`]: row[`${k}JournalCheckTargets`] ? keys(checkTargets).filter(_ => row[`${k}JournalCheckTargets`].includes(_)) : keys(checkTargets),
          };
        }, {}),
        defaultDebitTaxes: (row.defaultDebitTaxes ? availableTaxes.map(_ => _.name_ja).filter(_ => row.defaultDebitTaxes.includes(_)) : availableTaxes.map(_ => _.name_ja)),
        defaultCreditTaxes: (row.defaultCreditTaxes ? availableTaxes.map(_ => _.name_ja).filter(_ => row.defaultCreditTaxes.includes(_)) : availableTaxes.map(_ => _.name_ja)),
      }));
    });
    // NOTE: カスタム科目行がキー不足で列落ちしないよう、通常科目の完全なキー集合を基準列として明示する。
    const columns = mapKeysToJa(fromPairs(['accountName', 'category', ...(canReadAccountItemAuditorSetting(user) ? ['threshold'] : []), 'journalsCheckDimensions', ...keys(displayJournalTags).map(_ => `${_}JournalCheckTargets`), 'defaultDebitTaxes', 'defaultCreditTaxes', 'note'].map(_ => [_, null])));
    const fileContent = encoder.encode(unparseCsv({ fields: keys(columns), data: exportRows }));
    fileDownload(fileContent, `科目設定_${period}.csv`);
  }
  const onSelectFiles = async ({ target, target: { files: [file] } }) => {
    if(!file) return;

    toggleImporting(true);
    await new Promise((resolve) => {
      setTimeout(async () => {
        try {
          const fileContent = await readCsvFile(file);
          const { data } = parseCsv(fileContent, { header: true });
          const rows = data.map(_ => mapKeysFromJa(_, { メモ: 'note' }));
          if(canReadAccountItemAuditorSetting(user)) {
            await chunk(rows.filter(_ => _.threshold >= 0), 500).reduce(async (x, rows) => {
              await x;
              const batch = db.batch();
              rows.forEach((row) => {
                const { accountName, threshold } = row;
                batch.set(companiesRef.doc(companyId).collection('accountItemAuditorSettings').doc(`${accountName.replace(/\//g, '_')}__${period}`), { accountName, threshold: parseInt(threshold, 10), period });
              });
              await batch.commit();
            }, Promise.resolve());
          }
          await chunk(rows, 500).reduce(async (x, rows) => {
            await x;
            const batch = db.batch();
            rows.filter(_ => _.accountName).forEach((row) => {
              const { accountName, note, journalsCheckDimensions, defaultDebitTaxes, defaultCreditTaxes, } = row;
              batch.set(companiesRef.doc(companyId).collection('accountItemSettings').doc(`${accountName.replace(/\//g, '_')}`), omitBy({
                accountName,
                period: null,
                note,
                journalsCheckDimensions: journalsCheckDimensions.split(',').filter(_ => _).filter(_ => keys(displayJournalTags).includes(_)),
                ...keys(journalTags).reduce((x, k) => {
                  return {
                    ...x,
                    [`${k}JournalCheckTargets`]: row[`${k}JournalCheckTargets`]?.split(',').filter(_ => _).filter(_ => keys(checkTargets).includes(_)),
                  };
                }, {}),
                defaultDebitTaxes: defaultDebitTaxes.split(',').filter(_ => _).filter(_ => availableTaxes.map(_ => _.name_ja).includes(_)),
                defaultCreditTaxes: defaultCreditTaxes.split(',').filter(_ => _).filter(_ => availableTaxes.map(_ => _.name_ja).includes(_)),
              }, isUndefined));
            });
            await batch.commit();
          }, Promise.resolve());
          toast.success('インポートしました');
        } catch(e) {
          console.error(e);
          toast.error('インポート失敗しました');
        }
        toggleImporting(false);
        target.value = '';
        resolve();
      }, 300);
    });
  }
  const taxesByCode = keyBy(taxes, 'code');
  const accountItemNameOptions = sortedAccountItems.map(_ => ({ label: _.name, value: _.name, }));
  const periodOptions = ((company || {}).fiscalYears || []).map(_ => ({ label: `${_.start_date.replace(/-/g, '/').slice(0, 7)} - ${_.end_date.replace(/-/g, '/').slice(0, 7)}`, value: periodOfFiscalYear(_), }));
  const onClickCopyFromDefault = async () => {
    if(!window.confirm('freee経営管理デフォルトに従った設定にします。よろしいですか？')) return;

    const [accountItemDataByName, categoryDataByName] = await Promise.all(entries(accountItemSettingCsvUrls).map(async ([type, url]) => {
      const csv = await fetch(url).then(_ => _.text());
      const { data: rows } = parseCsv(csv, { header: true });
      return keyBy(rows, ({ accountItem: '科目', category: 'カテゴリ' })[type]);
    }));
    const newData = sortedAccountItems
      .map(_ => ({ ..._, accountItemSetting: accountItemSettingsById[`${_.name.replace(/\//g, '_')}`] }))
      .filter(_ => _.accountItemSetting == null)
      .map((accountItem) => {
        const { name, } = accountItem;
        const accountCategoryName = accountItem.account_category || last(accountItem.categories);
        const accountItemData = accountItemDataByName[name];
        const categoryData = categoryDataByName[accountCategoryName];
        return {
          ref: company.ref.collection('accountItemSettings').doc(`${name.replace(/\//g, '_')}`),
          ...mapValues(pick(mapKeysFromJa(accountItemData ?? categoryData), ['journalsCheckDimensions', ...keys(journalTags).map(_ => `${_}JournalCheckTargets`), 'defaultDebitTaxes', 'defaultCreditTaxes']), (v, k) => v.split(',').filter(_ => _).map(_ => _ === 'default_tax_code' ? taxesByCode[accountItem.default_tax_code]?.name_ja : _)),
          accountName: name,
          period: null,
        };
      });
    await batch(db, newData, (batch, _) => batch.set(_.ref, omit(_, 'ref')));
    toast.success('freee経営管理デフォルトに従った設定にしました。');
  };
  const onClickClear = async () => {
    if(!window.confirm('本当に設定をクリアしますか？')) return;

    await batch(db, [...accountItemSettings], (batch, _) => batch.delete(_.ref));
    toast.success('設定をクリアしました');
  };
  const customAccountItemsByName = keyBy(customAccountItems, 'name');
  // NOTE: カスタム科目IDから現在の配置カテゴリを逆引きする。CSVにカテゴリ指定がない行は既存配置を引き継ぐ。
  const categoryByCustomAccountItemId = fromPairs(entries(normalizedOrder).flatMap(([category, _]) => (_.customAccountItem || []).map(id => [id, category])));
  const validCategoryNames = new Set(accountItemCategories.map(_ => _.name));
  const processOrderSettingImport = async (data) => {
    // NOTE: カスタム科目はCSVの「カテゴリ」列でカテゴリを越えた並び替え（移動）が可能。明示的にcategoryキーへ寄せて曖昧さを排除する。
    const rows = data.map(_ => mapKeysFromJa(_, { メモ: 'note', カテゴリ: 'category' }));
    // NOTE: 名前一致で通常科目/カスタム科目を自動判別する。両方に同名が存在する行は曖昧なのでエラーで止める。
    const ambiguousNames = rows.map(_ => _.accountName).filter(name => accountItemsByName[name] && customAccountItemsByName[name]);
    if(ambiguousNames.length > 0) {
      throw new Error(`通常科目とカスタム科目に同名が存在します: ${[...new Set(ambiguousNames)].join(', ')}`);
    }
    // NOTE: 各行をエントリ化。通常科目は科目固有のカテゴリで固定。カスタム科目はCSVのカテゴリ列が有効ならそのカテゴリへ移動、なければ既存配置を引き継ぐ。どちらにも一致しない行は無視。
    const entriesWithCategory = rows.flatMap((row) => {
      const { accountName: name, category: csvCategory } = row;
      const accountItem = accountItemsByName[name];
      if(accountItem) return [{ category: accountItem.account_category, entry: { type: 'accountItem', name } }];
      const customAccountItem = customAccountItemsByName[name];
      if(customAccountItem) {
        const category = (csvCategory && validCategoryNames.has(csvCategory)) ? csvCategory : categoryByCustomAccountItemId[customAccountItem.id];
        return [{ category, entry: { type: 'customAccountItem', id: customAccountItem.id } }];
      }
      return [];
    });
    const entriesGroupedByCategory = groupBy(entriesWithCategory.filter(_ => _.category), 'category');
    // NOTE: CSVに登場したカスタム科目は移動元カテゴリから取り除く必要がある。移動元カテゴリがCSVに登場しないとそのカテゴリのbatchが走らず古い配置が温存されてしまうため、影響カテゴリを明示的に網羅する。
    const importedCustomIds = new Set(entriesWithCategory.flatMap(_ => _.entry.type === 'customAccountItem' ? [_.entry.id] : []));
    const sourceCategories = new Set(importedCustomIds.size > 0 ? [...importedCustomIds].map(id => categoryByCustomAccountItemId[id]).filter(_ => _) : []);
    const affectedCategories = [...new Set([...keys(entriesGroupedByCategory), ...sourceCategories])];
    await batch(db, affectedCategories, (b, category) => {
      const newEntries = (entriesGroupedByCategory[category] || []).map(_ => _.entry);
      const keptCustomItems = (normalizedOrder[category]?.customAccountItem || []).filter(id => !importedCustomIds.has(id)).map(id => ({ type: 'customAccountItem', id }));
      b.set(company.ref.collection('settings').doc('accountItemsOrder'), {
        [category]: [...newEntries, ...keptCustomItems],
      }, { merge: true });
    });
  };
  // NOTE: カテゴリの表示行から新形式エントリ配列を組み立てる。Firestore保存配列ではなく画面表示順を真として扱う。
  const buildEntriesFromRows = (categoryName) => {
    const group = rowGroups.find(_ => _.category.name === categoryName);
    if(!group) return [];
    return group.rows.map(_ => _.rowType === 'accountItem' ? { type: 'accountItem', name: _.accountName } : { type: 'customAccountItem', id: _.customAccountItem.id });
  };
  const moveRow = async ({ fromCategory, toCategory, fromIndex, toIndex }) => {
    const fromList = buildEntriesFromRows(fromCategory);
    const [moved] = fromList.splice(fromIndex, 1);
    if(!moved) return;
    if(fromCategory === toCategory) {
      fromList.splice(toIndex, 0, moved);
      await company.ref.collection('settings').doc('accountItemsOrder').set({
        [fromCategory]: fromList,
      }, { merge: true });
    } else {
      const toList = buildEntriesFromRows(toCategory);
      toList.splice(toIndex, 0, moved);
      await company.ref.collection('settings').doc('accountItemsOrder').set({
        [fromCategory]: fromList,
        [toCategory]: toList,
      }, { merge: true });
    }
  };
  const moveUncategorizedToCategory = async ({ customAccountItemId, toCategory, toIndex }) => {
    const toList = buildEntriesFromRows(toCategory);
    toList.splice(toIndex, 0, { type: 'customAccountItem', id: customAccountItemId });
    await company.ref.collection('settings').doc('accountItemsOrder').set({
      [toCategory]: toList,
    }, { merge: true });
  };
  const moveCategoryRowToUncategorized = async ({ fromCategory, fromIndex }) => {
    const fromList = buildEntriesFromRows(fromCategory);
    const [removed] = fromList.splice(fromIndex, 1);
    // NOTE: カスタム科目以外（通常科目）が未配置エリアに来るのは不正なので何もしない。
    if(!removed || removed.type !== 'customAccountItem') return;
    await company.ref.collection('settings').doc('accountItemsOrder').set({
      [fromCategory]: fromList,
    }, { merge: true });
  };

  return (
    <div className="company">
      <div className="container py-5">
        <div className="d-flex flex-column justify-content-center align-items-stretch">
          <div className="d-flex justify-content-end mb-3">
            <HelpLink text="科目設定を行う" />
          </div>
          <h3 className="text-center mb-4">科目設定</h3>
          <div className="d-flex justify-content-between flex-wrap mb-3 align-items-end">
            <div>
              <QuerySelector paramName="accountItemNames" width={300} isMulti options={accountItemNameOptions} label="勘定科目" />
            </div>
            <div className="d-flex flex-column gap-1">
              <div className="d-flex align-items-end justify-content-end gap-1">
                <ProgressButton color="primary" process={onClickCopyFromDefault}>
                  freee経営管理デフォルトに従う
                </ProgressButton>
                <ProgressButton color="danger" process={onClickClear}>
                  <span className="fas fa-eraser mr-1" />
                  設定をクリア
                </ProgressButton>
              </div>
              <div className="d-flex align-items-end justify-content-end">
                <ProgressButton icon={<span className="fas fa-download" />} color="secondary" process={onClickExport} className="ml-1">
                  エクスポート
                </ProgressButton>
                <Button color="secondary" className="ml-1" disabled={isImporting}> 
                  <label className="m-0 cursor-pointer">
                    <span className={classnames('fas mr-1', { 'fa-upload': !isImporting, 'fa-spin fa-spinner': isImporting})} />
                    インポート
                    <Input type="file" className="d-none" onChange={onSelectFiles} accept="text/*" />
                  </label>
                </Button>
                <ImportButton color="secondary" className="ml-1" label="並び替えインポート" onComplete={processOrderSettingImport} /> 
              </div>
            </div>
          </div>
          <div style={{ zIndex: 0 }}>
            {
              availableTaxes.length > 0 ? (
                <Fragment>
                  {
                    rowGroups.map((rowGroup) => {
                      const { category, filteredRows } = rowGroup;
                      return (
                        <RowGroup key={category.name} {...{ company, user, period, displayJournalTags, availableTaxes, category, rows: filteredRows, moveRow, moveCategoryRowToUncategorized, moveUncategorizedToCategory, customAccountItems, }} />
                      );
                    })
                  }
                  <UncategorizedCustomAccountItemsRowGroup
                    {...{ company, customAccountItems: uncategorizedCustomAccountItems, moveUncategorizedToCategory, accountItemsOrderSetting, rowGroups, }}
                  />
                </Fragment>
              ) : (
                <div className="text-center">
                  データはまだありません。
                </div>
              )
            }
          </div>
        </div>
      </div>
      <ToTop />
    </div>
  );
};

function RowGroup (props) {
  const { company, user, period, displayJournalTags, availableTaxes, category, rows, moveRow, moveUncategorizedToCategory, } = props;
  const getChildPayload = (index) => ({ fromCategory: category.name, fromIndex: index, row: rows[index], });
  const onDrop = async ({ addedIndex, removedIndex, payload }) => {
    if(removedIndex != null && addedIndex != null && (!payload || payload.fromCategory === category.name)) {
      // カテゴリ内移動
      await moveRow({ fromCategory: category.name, toCategory: category.name, fromIndex: removedIndex, toIndex: addedIndex });
      return;
    }
    if(addedIndex != null && payload && payload.fromCategory && payload.fromCategory !== category.name) {
      // 別カテゴリから来た
      await moveRow({ fromCategory: payload.fromCategory, toCategory: category.name, fromIndex: payload.fromIndex, toIndex: addedIndex });
      return;
    }
    if(addedIndex != null && payload && payload.fromUncategorized) {
      await moveUncategorizedToCategory({ customAccountItemId: payload.customAccountItemId, toCategory: category.name, toIndex: addedIndex });
      return;
    }
  };

  return (
    <div className="mb-2">
      <div className="bg-light border rounded-top px-3 py-2 font-weight-bold" style={{ position: 'sticky', top: 0, zIndex: 1, }}>
        {category.name}
      </div>
      <Container
        groupName="accountItemsOrder"
        dragHandleSelector=".drag-handle"
        getChildPayload={getChildPayload}
        onDrop={onDrop}
        dropPlaceholder={{ className: 'bg-light', }}
        render={(ref) => (
          <div ref={ref} className="border border-top-0 rounded-bottom">
            {
              rows.map((row) => {
                const key = row.rowType === 'accountItem' ? `acc__${row.accountName}` : `cus__${row.customAccountItem.id}`;
                return (
                  <Draggable
                    key={key}
                    render={() => (
                      <div>
                        {
                          row.rowType === 'accountItem' ? (
                            <Row {...{ row, company, period, displayJournalTags, availableTaxes, user, }} />
                          ) : (
                            <CustomAccountItemCard {...{ row, }} />
                          )
                        }
                      </div>
                    )}
                  />
                );
              })
            }
          </div>
        )}
      />
    </div>
  );
};

function UncategorizedCustomAccountItemsRowGroup (props) {
  const { customAccountItems, } = props;
  const getChildPayload = (index) => ({ fromUncategorized: true, customAccountItemId: customAccountItems[index]?.id });
  // NOTE: 未配置エリアからの並び替え（同じエリア内）は現状サポートしない。並び順の意味を持たないので。
  const onDrop = async () => {
    // 未配置エリアにドロップされた場合は何もしない
    return;
  };
  if(customAccountItems.length === 0) return null;
  return (
    <div className="mb-2 mt-4">
      <div className="bg-light border rounded-top px-3 py-2 text-muted small">
        未配置のカスタム科目（ドラッグでカテゴリに配置できます）
      </div>
      <Container
        groupName="accountItemsOrder"
        dragHandleSelector=".drag-handle"
        getChildPayload={getChildPayload}
        onDrop={onDrop}
        dropPlaceholder={{ className: 'bg-light', }}
        render={(ref) => (
          <div ref={ref} className="border border-top-0 rounded-bottom">
            {
              customAccountItems.map((customAccountItem) => (
                <Draggable
                  key={`uncategorized__${customAccountItem.id}`}
                  render={() => (
                    <div>
                      <CustomAccountItemCard {...{ row: { customAccountItem }, }} />
                    </div>
                  )}
                />
              ))
            }
          </div>
        )}
      />
    </div>
  );
}

// NOTE: カスタム科目は計算式/手入力の科目で、科目設定（チェックタグ・税区分等）を持たないため編集ボタンは出さない。
function CustomAccountItemCard (props) {
  const { row } = props;
  const { customAccountItem } = row;
  return (
    <div className="d-flex align-items-center border-bottom px-2 py-2">
      <div className="drag-handle text-muted cursor-pointer px-2">
        <span className="fas fa-grip-vertical" />
      </div>
      <div className="flex-grow-1">
        <span className="badge badge-info mr-1">カスタム</span>
        {customAccountItem.name}
        <span className="ml-2 small text-muted">
          {customAccountItem.isManual ? '(手入力)' : customAccountItem.expression}
        </span>
      </div>
    </div>
  );
}

const Row = React.memo(function (props) {
  const { row, period, company, displayJournalTags, availableTaxes, user, } = props;
  const { accountName, accountItemSetting, accountItemAuditorSetting, } = row;
  const { elm, focusColor, } = useScrollFocusByQuery(_ => _.queryParams.accountName === accountName);
  const accountItemSettingRef = company.ref.collection('accountItemSettings').doc(`${accountName.replace(/\//g, '_')}`);
  const displayThreshold = canReadAccountItemAuditorSetting(user);
  // NOTE: 設定値の編集は全てこのモーダルに集約する。基準値（threshold）だけ保存先が別コレクション（accountItemAuditorSettings, period付き）なので振り分ける。
  const processValues = _ => ({ ...omit(_, ['threshold']), accountName, period: null, });
  const withBatch = (batch, values) => {
    if(!displayThreshold) return;
    const auditorRef = company.ref.collection('accountItemAuditorSettings').doc(`${accountName.replace(/\//g, '_')}__${period}`);
    if(values.threshold == null) {
      batch.set(auditorRef, omitBy({ ...omit(accountItemAuditorSetting, 'id'), accountName, threshold: null, period }, isUndefined), { merge: true });
    } else {
      batch.set(auditorRef, { ...omit(accountItemAuditorSetting, 'id'), accountName, threshold: values.threshold, period }, { merge: true });
    }
  };
  // NOTE: 並び替え画面の要約表示。チェックタグ・税区分の「デフォルト」は空設定時とfreee経営管理デフォルト適用時で基準が異なり
  //       「変更あり」の判定が曖昧になるためバッジには出さない（詳細はモーダルで確認・編集する）。
  //       基準値（accountItemAuditorSettings）はユーザーが明示的に設定する監査値なので要約として出す。
  const hasThreshold = displayThreshold && row.threshold != null && row.threshold !== '';

  return (
    <div ref={elm} id={`row-${accountName}`} className="d-flex align-items-center border-bottom px-2 py-2" style={{ backgroundColor: focusColor, }}>
      <div className="drag-handle text-muted cursor-pointer px-2">
        <span className="fas fa-grip-vertical" />
      </div>
      <div className="flex-grow-1">
        {accountName}
        {
          row.note && (
            <div className="small text-muted" style={{ whiteSpace: 'pre-line' }}>
              {row.note}
            </div>
          )
        }
        {
          hasThreshold && (
            <div className="mt-1">
              <span className="badge badge-warning">基準値: {row.threshold}</span>
            </div>
          )
        }
      </div>
      <EditButton
        itemRef={accountItemSettingRef}
        FormModal={ModelFormModal}
        processValues={processValues}
        withBatch={withBatch}
        formProps={{ title: '科目設定 編集', values: { ...accountItemSetting, ...(displayThreshold && { threshold: row.threshold }), }, fields: fields({ displayJournalTags, availableTaxes, displayThreshold, }), }}
        usesSet
      />
    </div>
  );
}, isEqual);

CompanyAccountItemSettings.MonthSelectDisabled = true;

export default CompanyPage(CompanyAccountItemSettings);
